import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PROJECTS } from "@/lib/constants";

const ease = [0.16, 1, 0.3, 1];

/**
 * Drag-able before / after slider.
 * - Mouse drag, touch drag, AND single-click/tap to position the divider.
 * - Center handle indicates dragging. Works on mobile.
 */
const DragSlider = ({ before, after, alt }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(p);
  }, []);

  // Global listeners so drag continues even if cursor leaves the area.
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(clientX);
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
    };
  }, [setFromClientX]);

  const startDrag = (e) => {
    dragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(clientX);
  };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      className="relative w-full aspect-[4/5] sm:aspect-[16/9] overflow-hidden bg-charcoal-deep select-none touch-none cursor-ew-resize"
      style={{ touchAction: "none" }}
    >
      {/* AFTER (full) */}
      <img
        src={after}
        alt={`Rénovation intérieure ${alt}, après travaux par DEM BAT Genève, plâtrerie et peinture`}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
        loading="eager"
      />

      {/* BEFORE (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={`Avant rénovation appartement ${alt}, chantier plâtrerie isolation à Genève`}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
          loading="eager"
        />
      </div>

      {/* AVANT label */}
      <div className="absolute top-5 left-5 sm:top-7 sm:left-7 flex items-center gap-3 pointer-events-none">
        <span className="h-px w-8 bg-white/70" />
        <span className="text-white text-[10px] sm:text-xs tracking-[0.4em] uppercase font-medium drop-shadow">
          Avant
        </span>
      </div>

      {/* APRES label */}
      <div className="absolute top-5 right-5 sm:top-7 sm:right-7 flex items-center gap-3 pointer-events-none">
        <span className="text-bronze-light text-[10px] sm:text-xs tracking-[0.4em] uppercase font-medium drop-shadow">
          Après
        </span>
        <span className="h-px w-8 bg-bronze-light" />
      </div>

      {/* Divider line + draggable handle */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-bronze pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-bronze shadow-2xl ring-4 ring-white/20 flex items-center justify-center">
          <ChevronLeft className="w-4 h-4 text-white -mr-0.5" strokeWidth={2} />
          <ChevronRight className="w-4 h-4 text-white -ml-0.5" strokeWidth={2} />
        </div>
      </div>

      {/* First-time hint */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-charcoal-deep/70 backdrop-blur-sm text-white/90 text-[10px] tracking-[0.3em] uppercase font-medium pointer-events-none">
        Glissez pour comparer
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const project = PROJECTS[0];

  return (
    <section
      id="realisations"
      data-testid="before-after-section"
      className="bg-cream-alt py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease }}
          className="max-w-2xl mb-12 lg:mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="text-bronze font-serif text-2xl italic">§</span>
            <span className="eyebrow">Nos réalisations</span>
          </div>
          <h2 className="font-serif text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-normal">
            Avant. <span className="italic text-bronze">Après.</span>
            <br />
            La même <span className="italic">main.</span>
          </h2>
        </motion.div>

        {/* Slider */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1, ease }}
        >
          <DragSlider before={project.before} after={project.after} alt={`${project.location} ${project.year}`} />

          {/* Simplified info bar — Genève · 2024 only */}
          <div
            data-testid="before-after-info"
            className="relative bg-charcoal-deep text-white flex items-center justify-between gap-6 px-6 sm:px-10 py-6 sm:py-8"
          >
            <div className="flex items-baseline gap-4 sm:gap-6">
              <span className="text-bronze text-[10px] uppercase tracking-[0.3em]">Lieu · Année</span>
            </div>
            <div className="font-serif text-xl sm:text-2xl lg:text-3xl flex items-baseline gap-3">
              {project.location}
              <span className="text-bronze">·</span>
              <span className="italic text-white/80">{project.year}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
